import { NavLink, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Sliders, Users, ListChecks, Settings, LogOut, Skull, PlayCircle, ExternalLink } from 'lucide-react';
import { motion } from 'framer-motion';
import { useMafiaAuth } from '@/context/MafiaAuthContext';

const NAV = [
  { to: '/host', exact: true, icon: LayoutDashboard, label: 'Обзор', testid: 'host-nav-overview' },
  { to: '/host/games', icon: PlayCircle, label: 'Игры', testid: 'host-nav-games' },
  { to: '/host/scoring', icon: Sliders, label: 'Очки', testid: 'host-nav-scoring' },
  { to: '/host/players', icon: Users, label: 'Игроки', testid: 'host-nav-players' },
  { to: '/host/roles', icon: ListChecks, label: 'Библиотека ролей', testid: 'host-nav-roles' },
  { to: '/host/settings', icon: Settings, label: 'Настройки', testid: 'host-nav-settings' },
];

export default function HostLayout({ children, title, subtitle, actions }) {
  const navigate = useNavigate();
  const { host, logoutHost } = useMafiaAuth();

  return (
    <div className="ms-page" data-testid="host-layout">
      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] min-h-screen">
        <aside className="lg:sticky lg:top-0 lg:h-screen flex flex-col" style={{ backgroundColor: 'var(--ms-surface-1)', borderRight: '1px solid var(--ms-border-subtle)' }}>
          <button onClick={() => navigate('/mafia')} className="px-5 h-16 ms-border-b flex items-center gap-3 hover:bg-[var(--ms-surface-2)] transition-colors">
            <div className="w-8 h-8 flex items-center justify-center" style={{ background: 'var(--ms-red-soft)', border: '1px solid rgba(220,38,38,0.35)', borderRadius: 'var(--ms-radius-sm)' }}>
              <Skull size={14} className="ms-text-red" />
            </div>
            <div className="text-left leading-tight">
              <p className="ms-display text-sm uppercase tracking-wide">Хост-консоль</p>
              <p className="text-[10px] ms-text-3 mt-0.5">Мафия Салон</p>
            </div>
          </button>

          {host && (
            <div className="mx-3 mt-4 p-3 ms-border" style={{ borderRadius: 'var(--ms-radius-md)' }}>
              <p className="ms-kicker ms-kicker-red">Вход выполнен</p>
              <p className="text-sm ms-text font-medium truncate mt-1">{host.name || 'Ведущий'}</p>
              <p className="text-[11px] ms-text-3 truncate">{host.email}</p>
            </div>
          )}

          <nav className="flex-1 px-3 py-4 space-y-1">
            <p className="ms-kicker px-3 mb-2">Меню</p>
            {NAV.map((item) => (
              <NavLink key={item.to} to={item.to} end={item.exact} data-testid={item.testid}
                className={({ isActive }) => `relative flex items-center gap-3 px-3 py-2.5 text-sm font-medium transition-colors ${isActive ? 'ms-text' : 'ms-text-2 hover:ms-text'}`}
                style={({ isActive }) => isActive ? { background: 'var(--ms-surface-2)', borderRadius: 'var(--ms-radius-md)', border: '1px solid var(--ms-border-subtle)' } : { borderRadius: 'var(--ms-radius-md)' }}
              >
                {({ isActive }) => (
                  <>
                    {isActive && <motion.span layoutId="host-nav-bar" className="absolute left-0 top-1.5 bottom-1.5 w-0.5" style={{ background: 'var(--ms-red)', borderRadius: '2px' }} />}
                    <item.icon size={15} className={isActive ? 'ms-text-red' : ''} />
                    <span>{item.label}</span>
                  </>
                )}
              </NavLink>
            ))}
          </nav>

          <div className="px-3 py-4 ms-border-t space-y-1">
            <button onClick={() => navigate('/mafia')} className="w-full flex items-center gap-3 px-3 py-2 text-xs font-medium ms-text-2 hover:ms-text hover:bg-[var(--ms-surface-2)] transition-colors" style={{ borderRadius: 'var(--ms-radius-md)' }}>
              <ExternalLink size={13} /> Открыть Салон
            </button>
            <button onClick={async () => { await logoutHost(); navigate('/mafia'); }} data-testid="host-nav-signout"
              className="w-full flex items-center gap-3 px-3 py-2 text-xs font-medium ms-text-2 hover:text-rose-400 hover:bg-[var(--ms-surface-2)] transition-colors" style={{ borderRadius: 'var(--ms-radius-md)' }}>
              <LogOut size={13} /> Выйти
            </button>
          </div>
        </aside>

        <main className="min-w-0 flex flex-col">
          <header className="sticky top-0 z-10 backdrop-blur-xl" style={{ background: 'rgba(12,10,9,0.75)', borderBottom: '1px solid var(--ms-border-subtle)' }}>
            <div className="px-6 lg:px-10 h-16 flex items-center justify-between gap-4">
              <div className="min-w-0">
                {subtitle && <p className="ms-kicker ms-kicker-red truncate">{subtitle}</p>}
                <h1 className="ms-display text-xl sm:text-2xl mt-0.5 truncate">{title}</h1>
              </div>
              {actions && <div className="flex items-center gap-2 shrink-0">{actions}</div>}
            </div>
          </header>
          <motion.div className="px-6 lg:px-10 py-8 flex-1" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, ease: 'easeOut' }}>
            {children}
          </motion.div>
        </main>
      </div>
    </div>
  );
}
